import * as React from "react";

import { MembershipValidityPeriod, Nullable } from "@simplyk/common";
import { UseFormSetValue } from "react-hook-form";

import { Event } from "../components/PaymentProcessor/type";

import { GetCurrentUserFrontendObject } from "./types";

import { TicketingPaymentInput } from "@/types/ticketing";
import {
  QuestionOutput,
  TicketingFrontendPageOutput,
  TicketingFieldOutput,
  DiscountOutput,
  OccurrenceWithRatesOutput,
  TicketingDonationFormOutput,
} from "@/types/trpc";

export interface FullRateError {
  id: string;
  remainingTickets: number;
}

export interface MembershipRenewalBuyerInfo {
  expirationDate: Date | undefined;
  uniquelySelectedTicketIndex: number;
  shouldProposeAutomaticRenewal: boolean | undefined;
  validityPeriod: MembershipValidityPeriod | null;
}

export interface FrontendTicketingContextValues {
  ticketingIsFull: boolean;
  ticketingIsClosed: boolean;
  ticketingDescriptionIsOpen: boolean;
  openTicketingDescription: () => void;
  closeTicketingDescription: () => void;
  buyerDrawerIsOpen: boolean;
  toggleBuyerDrawer: (_setValue: UseFormSetValue<TicketingPaymentInput>) => () => void;
  fallBackToTicketingContainerOnFullTicketing: (_setValue: UseFormSetValue<TicketingPaymentInput>) => void;
  fallBackToTicketsDrawerOnOverpassedRatesSeats: (
    _fullRates: FullRateError[],
    _setValue: UseFormSetValue<TicketingPaymentInput>
  ) => void;
  fallBackToTicketsDrawerOnOverPassedTicketingSeats: (
    _remainingTicketingSeats: number,
    _setValue: UseFormSetValue<TicketingPaymentInput>
  ) => void;
  fallBackToTicketingContainerOnNoTicketSelected: (_setValue: UseFormSetValue<TicketingPaymentInput>) => void;
  fullRatesReturnByOrder: FullRateError[];
  remainingTicketingSeatsReturnByOrder: number | null;
  remainingTicketingSeats: number;
  hasDiscountCodes: boolean;
  validDiscount: DiscountOutput | null;
  setValidDiscount: (_discountCode: DiscountOutput | null) => void;
  ticketing: TicketingFrontendPageOutput;
  ticketingField: TicketingFieldOutput | undefined;
  organization: NonNullable<GetCurrentUserFrontendObject["currentOrganization"]>;
  questions: QuestionOutput[];
  registrationIncreaseThermometer?: boolean;
  isOnboarding: boolean;
  setSelectedOccurrenceId: (_occurrenceId: string | null) => void;
  selectedOccurrenceId: Nullable<string>;
  selectedOccurrenceWithRates: Nullable<OccurrenceWithRatesOutput>;
  occurrencesWithRates: OccurrenceWithRatesOutput[];
  hasADate: boolean;
  isOccurrencesWithRatesLoading: boolean;
  hasOnlyOneDate: boolean;
  isCampaignRegistration: boolean;
  donationForm: TicketingDonationFormOutput | undefined;
  event: Event | undefined;
  isAuction: boolean;
  membershipRenewalBuyerInfo: MembershipRenewalBuyerInfo | undefined;
  setMembershipRenewalBuyerInfo: (membershipRenewalBuyerInfo: MembershipRenewalBuyerInfo | undefined) => void;
}

const defaultValues: FrontendTicketingContextValues = {
  ticketingIsFull: false,
  ticketingIsClosed: false,
  ticketingDescriptionIsOpen: false,
  openTicketingDescription: () => {},
  closeTicketingDescription: () => {},
  buyerDrawerIsOpen: false,
  toggleBuyerDrawer: (_setValue: UseFormSetValue<TicketingPaymentInput>) => () => {},
  fallBackToTicketingContainerOnFullTicketing: (_setValue: UseFormSetValue<TicketingPaymentInput>) => {},
  fallBackToTicketsDrawerOnOverpassedRatesSeats: (
    _fullRates: FullRateError[],
    _setValue: UseFormSetValue<TicketingPaymentInput>
  ) => {},
  fallBackToTicketsDrawerOnOverPassedTicketingSeats: (
    _remainingTicketingSeats: number,
    _setValue: UseFormSetValue<TicketingPaymentInput>
  ) => {},
  fallBackToTicketingContainerOnNoTicketSelected: (_setValue: UseFormSetValue<TicketingPaymentInput>) => {},
  fullRatesReturnByOrder: [] as FullRateError[],
  remainingTicketingSeatsReturnByOrder: null as number | null,
  remainingTicketingSeats: Infinity,
  hasDiscountCodes: false,
  validDiscount: null as DiscountOutput | null,
  setValidDiscount: (_discountCode: DiscountOutput | null) => {},
  ticketing: {} as TicketingFrontendPageOutput,
  ticketingField: {} as TicketingFieldOutput,
  organization: {} as NonNullable<GetCurrentUserFrontendObject["currentOrganization"]>,
  questions: [] as QuestionOutput[],
  registrationIncreaseThermometer: false,
  isOnboarding: false,
  selectedOccurrenceWithRates: null,
  occurrencesWithRates: [],
  hasADate: false,
  isOccurrencesWithRatesLoading: true,
  setSelectedOccurrenceId: (_occurrenceId: string | null) => {},
  selectedOccurrenceId: null,
  hasOnlyOneDate: false,
  isCampaignRegistration: false,
  donationForm: undefined,
  event: undefined,
  isAuction: false,
  membershipRenewalBuyerInfo: undefined,
  setMembershipRenewalBuyerInfo: () => {},
};
export const FrontendTicketingContext = React.createContext<FrontendTicketingContextValues>(defaultValues);
